import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Col, Flex, Popover, Row, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { ClientRes, TOrderRes, UserRes } from 'api/swagger';
import { UserRole, useRoleGuard } from 'shared/hooks/use-role-guard.ts';
import { useTransformToTableData } from 'shared/hooks/use-transform-to-table-data';
import { formatWithPostfixOrEmpty } from 'shared/utils/format-with-postfix-or-empty';
import { round100 } from 'shared/utils/numbers/round.ts';
import { formatDateTime } from 'shared/utils/time/format-date-time.ts';
import { OrderStatus } from 'types/order.ts';
import { ClientFinancialTag } from 'components/clients/client-financial-tag';
import { HiddenData } from 'components/hidden-data';
import { OrderStatusTag } from 'components/orders/order-status-tag';
import { OrderHasMainInvoiceIcon } from '../order-has-main-invoice-icon';
import styles from './orders-list.module.scss';
import type { IOrdersTableProps } from './types';

export const OrdersList: React.FC<IOrdersTableProps> = (props) => {
  const { orders, isLoading, withoutClient = false } = props;

  const isAllowFinancialInfo = useRoleGuard([
    UserRole.admin,
    UserRole.assistant,
    UserRole.customer_manager,
    UserRole.accountant,
  ]);
  const isAllowProfitByMap = useRoleGuard([UserRole.admin]);

  const { t } = useTranslation();
  const tableData = useTransformToTableData(orders);

  const columns: ColumnsType<TOrderRes> = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: '10%',
      render: (id: string, { clientOrderNumber, compositeId, mainInvoiceId, status }) => (
        <Row align='middle' gutter={12}>
          <Col>
            <Link to={`/orders/${id}`}>{compositeId}</Link>
            <Typography.Text className={styles.secondary}>{clientOrderNumber}</Typography.Text>
          </Col>
          <Col>
            <OrderHasMainInvoiceIcon isShow={status === OrderStatus.delivered} hasMainInvoice={!!mainInvoiceId} />
          </Col>
        </Row>
      ),
    },
    {
      title: t('common:status'),
      dataIndex: 'status',
      width: '10%',
      render: (status) => {
        return <OrderStatusTag status={status} />;
      },
    },
    {
      title: t('order:freight'),
      dataIndex: 'freightInDefault',
      width: '7%',
      render: (freightInDefault: number, { fineInDefault }) => {
        if (!isAllowFinancialInfo) return <HiddenData />;
        return (
          <>
            <Typography.Text>{freightInDefault} €</Typography.Text>
            {fineInDefault ? <Typography.Text className={styles.secondary}>+ {fineInDefault} €</Typography.Text> : null}
          </>
        );
      },
    },
    {
      title: t('order:docs'),
      dataIndex: 'documentPrice',
      width: '7%',
      render: (documentPrice: number) => {
        if (!isAllowFinancialInfo) return <HiddenData />;
        return <Typography.Text>{documentPrice ? `${documentPrice} €` : '-'}</Typography.Text>;
      },
    },
    {
      title: t('order:cost_price'),
      dataIndex: 'costPrice',
      width: '7%',
      render: (costPrice: number, { costPriceByMap }) => {
        if (!isAllowFinancialInfo) return <HiddenData />;

        if (!costPrice && !costPriceByMap) {
          return null;
        }

        return (
          <>
            <div>{formatWithPostfixOrEmpty(costPrice, '€')}</div>
            {isAllowProfitByMap && (
              <div className={styles.secondary}>{formatWithPostfixOrEmpty(costPriceByMap, '€')}</div>
            )}
          </>
        );
      },
    },
    {
      title: t('order:profit'),
      dataIndex: 'profit',
      width: '10%',
      render: (
        _,
        { costPrice, costPriceByMap, documentPriceInDefault, freightInDefault, fineInDefault, additionalCost },
      ) => {
        if (!isAllowFinancialInfo) return <HiddenData />;

        const profit = round100(
          freightInDefault +
            (documentPriceInDefault ?? 0) +
            (fineInDefault ?? 0) -
            (costPrice ?? 0) -
            (additionalCost ?? 0),
        );
        const profitByMap = round100(
          freightInDefault +
            (documentPriceInDefault ?? 0) +
            (fineInDefault ?? 0) -
            (costPriceByMap ?? 0) -
            (additionalCost ?? 0),
        );

        return (
          <>
            <div>{costPrice && freightInDefault ? `${profit} €` : '-'}</div>
            {isAllowProfitByMap && (
              <div className={styles.secondary}>{costPriceByMap && freightInDefault ? `${profitByMap} €` : '-'}</div>
            )}
          </>
        );
      },
    },
    ...(!withoutClient
      ? ([
          {
            title: t('client:client'),
            dataIndex: 'client',
            ellipsis: true,
            render: (client: ClientRes, { paymentPeriod, paymentTerms }) => {
              return (
                <Popover
                  content={
                    <>
                      <div>VAT: {client?.vatNumber}</div>
                      <div>Type: {client?.type}</div>
                    </>
                  }
                  title={client?.name}
                  trigger='click'>
                  <Flex align='flex-start' vertical>
                    <Typography.Text>{client?.shortName || client?.name}</Typography.Text>
                    <ClientFinancialTag
                      paymentPeriod={paymentPeriod}
                      paymentTerms={paymentTerms}
                      clientPaymentPeriod={client.paymentPeriod}
                      clientPaymentTerms={client.paymentTerms}
                    />
                  </Flex>
                </Popover>
              );
            },
          },
        ] as ColumnsType<TOrderRes>)
      : []),
    {
      title: `${t('common:manager')} / ${t('common:created')}`,
      dataIndex: 'user',
      ellipsis: true,
      width: '20%',
      render: (user: UserRes, { createdAt }) => {
        return (
          <>
            <Typography.Text>{user?.name}</Typography.Text>
            <Typography.Text className={styles.secondary}>{formatDateTime(createdAt)}</Typography.Text>
          </>
        );
      },
    },
  ];

  return (
    <div>
      <Table
        rowClassName={(record) => (record.status === OrderStatus.cancelled ? styles.canceledRow : '')}
        size={'small'}
        columns={columns}
        dataSource={tableData}
        pagination={false}
        loading={isLoading ? { size: 'large', delay: 200 } : false}
        bordered
      />
    </div>
  );
};
